import SearchFormField from 'components/SearchV2/Components/SearchFormField/SearchFormField'
import FlightSearchWidgetDispatchContext from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetDispatchContext'
import FlightSearchWidgetStateContext from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateContext'
import { FlightSearchWidgetActions } from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateReducer'
import { StandaloneFlightMenu } from 'contexts/Flights/FlightSearchWidget/types'
import type { ComponentProps } from 'react'
import { useCallback, useContext, useEffect, useRef } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import clsx from 'clsx'
import FlightSearchAirportSelect from './FlightSearchAirportSelect'

const Container = styled.div`
  [data-floating-dropdown] > * {
    border-radius: ${props => props.theme.borderRadius.L};
  }
`

const TabletField = styled(FlightSearchAirportSelect)`
  border-block-end: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};

  > div > span {
    block-size: ${rem(48)};
    background-color: transparent;
    border: 0;
    border-radius: 0;
  }

  input {
    padding-inline: ${rem(16)} ${rem(72)};
  }

  .display-field {
    inset: 0;
    background-color: transparent;
    border-radius: 0;
    padding-inline: ${rem(16)} ${rem(72)};
  }

  span:has(+ .display-field):not(:focus-within) > input {
    color: transparent;

    &::placeholder {
      color: transparent;
    }
  }

  &.has-error {
    outline: 1px solid ${props => props.theme.palette.messaging.critical.normalForeground};
    outline-offset: -1px;
  }
`

interface Props extends ComponentProps<typeof SearchFormField> {
  inlineSearch: boolean
  flightId: string
  direction: 'departure' | 'arrival'
  readOnly?: boolean
  onNext: (flightId: string) => void
  'aria-label'?: string
}

function TabletAirportField({ flightId, direction, readOnly, onNext, ...props }: Props) {
  const state = useContext(FlightSearchWidgetStateContext)
  const dispatch = useContext(FlightSearchWidgetDispatchContext)
  const inputRef = useRef<HTMLInputElement>(null)
  const flight = state.flights.find(item => item.id === flightId)
  const isDeparture = direction === 'departure'
  const menu = isDeparture ? StandaloneFlightMenu.departAirport : StandaloneFlightMenu.arrivalAirport

  useEffect(() => {
    if (!readOnly && state.selectedFlightId === flightId && state.activeMenu === menu) {
      inputRef.current?.focus()
    }
  }, [flightId, menu, readOnly, state.activeMenu, state.selectedFlightId])

  const onSelectAirport = useCallback((airport?: App.AirportLocation) => {
    dispatch({
      type: isDeparture ? FlightSearchWidgetActions.SET_DEPARTURE_AIRPORT : FlightSearchWidgetActions.SET_ARRIVAL_AIRPORT,
      id: flightId,
      airport,
    })
    if (airport) onNext(flightId)
  }, [dispatch, flightId, isDeparture, onNext])

  if (!flight) return null

  return <Container>
    <TabletField
      ref={inputRef}
      direction={direction}
      dropdownSize="S"
      dropdownWidth="max"
      dropdownPlacement="bottom-start"
      dropdownAnchorOffset={0}
      airport={isDeparture ? flight.departureAirport : flight.arrivalAirport}
      alternateAirport={isDeparture ? flight.arrivalAirport : flight.departureAirport}
      onSelectAirport={onSelectAirport}
      readOnly={readOnly}
      required={props.required}
      requiredErrorMessage={props.requiredErrorMessage}
      placeholder={props.placeholder}
      aria-label={props['aria-label']}
      className={clsx({ 'has-error': props.hasError })}
    />
  </Container>
}

function FlightSearchResponsiveAirportField(props: Props) {
  const { inlineSearch, flightId, direction, readOnly, onNext, ...legacyProps } = props
  if (inlineSearch) return <TabletAirportField {...props} />
  return <SearchFormField {...legacyProps} />
}

export default FlightSearchResponsiveAirportField
